/* eslint-disable react/prop-types */
/**
 * @fileoverview ClubOS Scouting Dashboard — Chief Scout / Recruitment Analyst view.
 * Pipeline funnel, target watchlist, scout reports, scouting alerts, foreign slot status.
 * TODO: Replace mock data with API calls: scoutingService.getDashboard()
 */

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftProgress from "components/SoftProgress";
import SoftButton from "components/SoftButton";
import ClickableCard from "components/ClickableCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import { scoutingTargets, scoutReports, financialSummary, alerts, squad } from "data/mockData";
import { formatCompact, formatDateIN, CLUB_NAME, SEASON } from "utils/indiaLocale";

// ─── DERIVED DATA ────────────────────────────────────────────────────────────

const budgetRemaining = financialSummary.transferBudget - financialSummary.transferBudgetUsed;
const agreedDeals = scoutingTargets.filter((t) => t.stage === "Agreed").length;
const STAGES = ["Identified", "Watched", "Scouted", "Targeted", "Approached", "Agreed"];
const stageCounts = STAGES.map((s) => ({ stage: s, count: scoutingTargets.filter((t) => t.stage === s).length }));
const STAGE_COLORS = { Identified: "info", Watched: "primary", Scouted: "dark", Targeted: "warning", Approached: "error", Agreed: "success" };
const PRIORITY_COLORS = { High: "error", Medium: "warning", Low: "success" };
const scoutingAlerts = alerts.filter((a) => a.category === "Scouting");
const ALERT_ICON = { info: "info", warning: "warning", error: "error_outline" };
const ratingColor = (r) => (r >= 8.5 ? "#82d616" : r >= 7.5 ? "#17c1e8" : "#fb8c00");
const sortedTargets = [...scoutingTargets].sort((a, b) => STAGES.indexOf(b.stage) - STAGES.indexOf(a.stage));
const foreignPlayers = squad.filter((p) => p.nationality !== "Indian");
const hoverSx = { cursor: "pointer", borderRadius: "lg", transition: "transform 0.18s, box-shadow 0.18s", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(0,0,0,0.13)" } };

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function PanelCard({ title, subtitle, children }) {
  return (
    <Card sx={{ height: "100%" }}>
      <SoftBox p={3}>
        <SoftTypography variant="h6" fontWeight="medium">{title}</SoftTypography>
        {subtitle && <SoftTypography variant="caption" color="secondary">{subtitle}</SoftTypography>}
        <Divider sx={{ my: 1 }} />
        {children}
      </SoftBox>
    </Card>
  );
}

function AttrBar({ label, value }) {
  return (
    <SoftBox mb={0.5}>
      <SoftBox display="flex" justifyContent="space-between" mb={0.25}>
        <SoftTypography variant="caption" color="secondary" sx={{ textTransform: "capitalize" }}>{label}</SoftTypography>
        <SoftTypography variant="caption" fontWeight="medium">{value}</SoftTypography>
      </SoftBox>
      <SoftProgress value={value * 10} color="info" variant="gradient" />
    </SoftBox>
  );
}

function TargetRow({ t, showAttrs }) {
  return (
    <SoftBox mb={2.5}>
      <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
        <SoftBox>
          <SoftTypography variant="body2" fontWeight="bold">{t.flag} {t.name}</SoftTypography>
          <SoftTypography variant="caption" color="secondary">{t.club} · {t.position} · Age {t.age}</SoftTypography>
        </SoftBox>
        <SoftBox display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
          <SoftBadge badgeContent={t.stage} color={STAGE_COLORS[t.stage]} variant="gradient" size="xs" container />
          <SoftBadge badgeContent={t.priority} color={PRIORITY_COLORS[t.priority]} variant="gradient" size="xs" container />
        </SoftBox>
      </SoftBox>
      {showAttrs && t.attributes && Object.entries(t.attributes).map(([k, v]) => <AttrBar key={k} label={k} value={v} />)}
      <SoftBox display="flex" justifyContent="space-between" mt={0.5}>
        <SoftTypography variant="caption" color="secondary">Fee Estimate</SoftTypography>
        <SoftTypography variant="caption" fontWeight="medium">{formatCompact(t.feeEstimate)}</SoftTypography>
      </SoftBox>
      {t.notes && <SoftTypography variant="caption" color="secondary" sx={{ display: "block", mt: 0.5 }}>{t.notes}</SoftTypography>}
      <Divider sx={{ mt: 1.5 }} />
    </SoftBox>
  );
}

function ReportDetail({ rep }) {
  const rc = rep.recommendation === "Sign Now" ? "success" : rep.recommendation === "Monitor" ? "info" : "error";
  return (
    <SoftBox mb={2.5}>
      <SoftBox display="flex" justifyContent="space-between" mb={0.75}>
        <SoftBox>
          <SoftTypography variant="body2" fontWeight="bold">{rep.flag} {rep.player}</SoftTypography>
          <SoftTypography variant="caption" color="secondary">{rep.club} · Age {rep.age} · {rep.scout} · {formatDateIN(rep.date)}</SoftTypography>
        </SoftBox>
        <SoftBox textAlign="right">
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: ratingColor(rep.overallRating) }}>{rep.overallRating.toFixed(1)}<span style={{ fontSize: "0.7rem", color: "#8392ab" }}>/10</span></SoftTypography>
          <SoftBadge badgeContent={rep.recommendation} color={rc} variant="gradient" size="xs" container />
        </SoftBox>
      </SoftBox>
      {Object.entries(rep.attributes).map(([k, v]) => <AttrBar key={k} label={k} value={v} />)}
      <SoftBox display="flex" justifyContent="space-between" mt={0.75}><SoftTypography variant="caption" color="secondary">Fee Est.</SoftTypography><SoftTypography variant="caption" fontWeight="medium">{rep.feeEstimate ? formatCompact(rep.feeEstimate) : "—"}</SoftTypography></SoftBox>
      <SoftTypography variant="caption" color="secondary" sx={{ display: "block", mt: 0.5 }}>{rep.notes}</SoftTypography>
      <Divider sx={{ mt: 1.5 }} />
    </SoftBox>
  );
}

// ─── DETAIL CONTENT (module-level, stable references) ───────────────────────

const pipelineDetail = (
  <SoftBox>{STAGES.map((stage) => { const inStage = scoutingTargets.filter((t) => t.stage === stage); return inStage.length === 0 ? null : (
    <SoftBox key={stage} mb={2}>
      <SoftBadge badgeContent={`${stage} (${inStage.length})`} color={STAGE_COLORS[stage]} variant="gradient" size="sm" container />
      {inStage.map((t) => <SoftBox key={t.id} display="flex" justifyContent="space-between" pl={1} py={0.5}><SoftBox><SoftTypography variant="caption" fontWeight="medium">{t.flag} {t.name}</SoftTypography><SoftTypography variant="caption" color="secondary" sx={{ display: "block" }}>{t.club} · {t.position} · Age {t.age}</SoftTypography></SoftBox><SoftTypography variant="caption" fontWeight="medium">{formatCompact(t.feeEstimate)}</SoftTypography></SoftBox>)}
      <Divider sx={{ mt: 0.5 }} />
    </SoftBox>);
  })}</SoftBox>
);

const watchlistDetail = <SoftBox>{sortedTargets.map((t) => <TargetRow key={t.id} t={t} showAttrs />)}</SoftBox>;

const reportsDetail = <SoftBox>{scoutReports.map((rep) => <ReportDetail key={rep.id} rep={rep} />)}</SoftBox>;

const alertsDetail = (
  <SoftBox>{scoutingAlerts.length === 0 ? <SoftTypography variant="caption" color="secondary">No alerts.</SoftTypography>
    : scoutingAlerts.map((alert) => (
      <SoftBox key={alert.id} mb={2}>
        <SoftBox display="flex" alignItems="flex-start" gap={1} mb={0.75}>
          <Icon fontSize="small" sx={{ color: alert.type === "error" ? "#ea0606" : alert.type === "warning" ? "#fbcf33" : "#17c1e8", mt: 0.25, flexShrink: 0 }}>{ALERT_ICON[alert.type] || "info"}</Icon>
          <SoftBox flex={1}>
            <SoftBox display="flex" justifyContent="space-between"><SoftTypography variant="caption" fontWeight="bold">{alert.title}</SoftTypography><SoftTypography variant="caption" color="secondary">{alert.time}</SoftTypography></SoftBox>
            <SoftTypography variant="caption" color="secondary">{alert.description}</SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftTypography variant="caption" fontWeight="medium" sx={{ display: "block", pl: 3.5, mb: 0.5 }}>Action Items</SoftTypography>
        {(alert.actionItems || ["Review within 24 hours", "Notify Chief Scout", "Update recruitment log"]).map((item, i) => (
          <SoftBox key={i} display="flex" alignItems="center" gap={0.75} pl={3.5} mb={0.4}><Icon sx={{ fontSize: "0.85rem", color: "#17c1e8" }}>arrow_right</Icon><SoftTypography variant="caption">{item}</SoftTypography></SoftBox>
        ))}
        <Divider sx={{ mt: 1.25 }} />
      </SoftBox>
    ))}</SoftBox>
);

const foreignSlotDetail = (
  <SoftBox>
    <SoftTypography variant="body2" fontWeight="medium" mb={1}>ISL Foreign Player Rules</SoftTypography>
    {["Max 6 foreign players registered per squad.", "Max 4 outfield + 1 Asian foreign on pitch simultaneously (4+1 rule).", "Foreign GK counts within the 4-player outfield quota.", "All registrations must be lodged with AIFF before the transfer deadline."].map((r, i) => (
      <SoftBox key={i} display="flex" alignItems="flex-start" gap={0.75} mb={0.6}><Icon sx={{ fontSize: "0.85rem", color: "#17c1e8", mt: 0.25, flexShrink: 0 }}>info_outline</Icon><SoftTypography variant="caption">{r}</SoftTypography></SoftBox>
    ))}
    <Divider sx={{ my: 1.5 }} />
    <SoftTypography variant="body2" fontWeight="medium" mb={1}>Slot Usage ({foreignPlayers.length}/6)</SoftTypography>
    {foreignPlayers.map((p) => { const exp = p.contractExpiry <= "2026-06-01"; const sc = p.status === "Available" ? "success" : p.status === "Injured" ? "error" : "warning"; return (
      <SoftBox key={p.id} display="flex" justifyContent="space-between" alignItems="center" py={0.75} sx={{ borderBottom: "1px solid #f0f0f0" }}>
        <SoftBox display="flex" alignItems="center" gap={1}><span style={{ fontSize: 16 }}>{p.flag}</span><SoftBox><SoftTypography variant="caption" fontWeight="medium">{p.name}</SoftTypography><SoftTypography variant="caption" color="secondary" sx={{ display: "block" }}>{p.position} · {p.nationality} · Expires {p.contractExpiry}</SoftTypography></SoftBox></SoftBox>
        <SoftBox display="flex" gap={0.5}>{exp && <SoftBadge badgeContent="Expiring" color="warning" variant="gradient" size="xs" container />}<SoftBadge badgeContent={p.status} color={sc} variant="gradient" size="xs" container /></SoftBox>
      </SoftBox>);})}
  </SoftBox>
);

const agreedTarget = scoutingTargets.find((x) => x.stage === "Agreed");
const agreedDetail = agreedTarget ? (
  <SoftBox>
    <SoftBox display="flex" alignItems="center" gap={1} mb={2}><span style={{ fontSize: 28 }}>{agreedTarget.flag}</span><SoftBox><SoftTypography variant="h6" fontWeight="bold">{agreedTarget.name}</SoftTypography><SoftTypography variant="caption" color="secondary">{agreedTarget.club} · {agreedTarget.position} · Age {agreedTarget.age}</SoftTypography></SoftBox></SoftBox>
    {[["Priority", agreedTarget.priority], ["Fee Estimate", formatCompact(agreedTarget.feeEstimate)], ["Nationality", agreedTarget.nationality || "—"], ["Stage", agreedTarget.stage]].map(([label, value]) => (
      <SoftBox key={label} display="flex" justifyContent="space-between" py={0.5} sx={{ borderBottom: "1px solid #f0f0f0" }}><SoftTypography variant="caption" color="secondary">{label}</SoftTypography><SoftTypography variant="caption" fontWeight="medium">{value}</SoftTypography></SoftBox>
    ))}
    <SoftTypography variant="body2" fontWeight="medium" mt={2} mb={0.75}>Next Steps</SoftTypography>
    {["Complete medical examination", "Finalise personal terms", "Submit ITC request to AIFF", "Register with ISL before deadline"].map((step) => (
      <SoftBox key={step} display="flex" alignItems="center" gap={0.75} mb={0.5}><Icon sx={{ fontSize: "0.9rem", color: "#82d616" }}>check_circle_outline</Icon><SoftTypography variant="caption">{step}</SoftTypography></SoftBox>
    ))}
  </SoftBox>
) : <SoftTypography variant="caption" color="secondary">No agreed deals at this time.</SoftTypography>;

const KPI_DETAILS = { pipeline: pipelineDetail, reports: reportsDetail, budget: (
  <SoftBox>
    <Grid container spacing={2} mb={2}>{[["Total Budget", financialSummary.transferBudget, "info"], ["Spent", financialSummary.transferBudgetUsed, "error"], ["Remaining", budgetRemaining, "success"]].map(([label, val, color]) => (
      <Grid item xs={4} key={label}><Card variant="outlined" sx={{ p: 1.5, textAlign: "center" }}><SoftTypography variant="caption" color="secondary">{label}</SoftTypography><SoftTypography variant="h6" fontWeight="bold" color={color}>{formatCompact(val)}</SoftTypography></Card></Grid>
    ))}</Grid>
    <SoftTypography variant="body2" fontWeight="medium" mb={1}>Transfer History</SoftTypography>
    {(financialSummary.transferHistory || []).map((tx, i) => (
      <SoftBox key={i} display="flex" justifyContent="space-between" py={0.75} sx={{ borderTop: "1px solid #f0f0f0" }}><SoftBox><SoftTypography variant="caption" fontWeight="medium">{tx.player}</SoftTypography><SoftTypography variant="caption" color="secondary" sx={{ display: "block" }}>{tx.type} · {tx.club}</SoftTypography></SoftBox><SoftTypography variant="caption" fontWeight="medium">{formatCompact(tx.fee)}</SoftTypography></SoftBox>
    ))}
  </SoftBox>
), agreed: agreedDetail };
const KPI_TITLE = { pipeline: "Targets in Pipeline — Full Breakdown", reports: "All Scout Reports", budget: "Transfer Budget Breakdown", agreed: "Agreed Deal — Details & Next Steps" };

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

function ScoutingDashboard() {
  const [kpiModal, setKpiModal] = useState(null);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* ── Page Header ─────────────────────────────────────────────────── */}
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold">Scouting &amp; Recruitment</SoftTypography>
            <SoftTypography variant="body2" color="secondary">{CLUB_NAME} · Scouting Department · Season {SEASON}</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} flexWrap="wrap">
            <SoftBadge badgeContent="Summer Window: Jun 1 – Aug 31" color="info" variant="gradient" size="sm" container />
            <SoftBadge badgeContent="Winter: Jan 1 – Feb 15" color="secondary" variant="gradient" size="sm" container />
          </SoftBox>
        </SoftBox>

        {/* ── Row 1: KPI Cards ─────────────────────────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          {[
            { key: "pipeline", title: "Targets in Pipeline", count: scoutingTargets.length, pct: { color: "info", text: "across 6 stages" }, icon: { component: "radar", color: "info" } },
            { key: "reports",  title: "Reports Filed",        count: scoutReports.length,    pct: { color: "primary", text: "this season" },    icon: { component: "assignment", color: "primary" } },
            { key: "budget",   title: "Budget Remaining",     count: formatCompact(budgetRemaining), pct: { color: "success", text: "for transfers" }, icon: { component: "payments", color: "success" } },
            { key: "agreed",   title: "Agreed Deals",         count: agreedDeals,             pct: { color: "warning", text: "pending paperwork" }, icon: { component: "handshake", color: "warning" } },
          ].map(({ key, title, count, pct, icon }) => (
            <Grid item xs={12} sm={6} lg={3} key={key}>
              <SoftBox onClick={() => setKpiModal(key)} sx={hoverSx}>
                <MiniStatisticsCard title={{ text: title }} count={count} percentage={pct} icon={icon} />
              </SoftBox>
            </Grid>
          ))}
        </Grid>

        {/* ── Row 2: Pipeline Funnel + Target Watchlist ────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={4}>
            <ClickableCard title="Pipeline Funnel" icon="account_tree" iconColor="info" tooltip="All targets at each recruitment stage" detailContent={pipelineDetail}>
              <PanelCard title="Pipeline Funnel" subtitle="Targets per stage">
                {stageCounts.map(({ stage, count }) => {
                  const pct = scoutingTargets.length > 0 ? Math.round((count / scoutingTargets.length) * 100) : 0;
                  return (
                    <SoftBox key={stage} mb={1.5}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <SoftTypography variant="caption" fontWeight="medium">{stage}</SoftTypography>
                        <SoftBadge badgeContent={count} color={STAGE_COLORS[stage]} variant="gradient" size="xs" container />
                      </SoftBox>
                      <SoftProgress value={pct} color={STAGE_COLORS[stage]} variant="gradient" />
                    </SoftBox>
                  );
                })}
              </PanelCard>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} lg={8}>
            <ClickableCard title="Target Watchlist" icon="visibility" iconColor="primary" tooltip="Full scouting profiles for all targets" detailContent={watchlistDetail}>
              <PanelCard title="Target Watchlist" subtitle="All scouting targets — sorted by stage">
                <SoftBox sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>{["Player", "Age", "Pos", "Stage", "Priority", "Fee Est.", "Action"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>
                          <SoftTypography variant="caption" color="secondary" fontWeight="bold">{h}</SoftTypography>
                        </th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {sortedTargets.map((t) => (
                        <tr key={t.id} style={{ borderTop: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "8px", minWidth: 160 }}>
                            <SoftBox display="flex" alignItems="center" gap={0.5}>
                              <span style={{ fontSize: 16 }}>{t.flag}</span>
                              <SoftBox><SoftTypography variant="caption" fontWeight="medium" sx={{ display: "block" }}>{t.name}</SoftTypography><SoftTypography variant="caption" color="secondary">{t.club}</SoftTypography></SoftBox>
                            </SoftBox>
                          </td>
                          <td style={{ padding: "8px" }}><SoftTypography variant="caption">{t.age}</SoftTypography></td>
                          <td style={{ padding: "8px" }}><SoftTypography variant="caption" fontWeight="medium">{t.position}</SoftTypography></td>
                          <td style={{ padding: "8px" }}><SoftBadge badgeContent={t.stage} color={STAGE_COLORS[t.stage]} variant="gradient" size="xs" container /></td>
                          <td style={{ padding: "8px" }}><SoftBadge badgeContent={t.priority} color={PRIORITY_COLORS[t.priority]} variant="gradient" size="xs" container /></td>
                          <td style={{ padding: "8px", whiteSpace: "nowrap" }}><SoftTypography variant="caption">{formatCompact(t.feeEstimate)}</SoftTypography></td>
                          <td style={{ padding: "8px" }}><Tooltip title="View full profile"><Icon sx={{ cursor: "pointer", color: "#17c1e8", fontSize: "1.1rem" }}>open_in_new</Icon></Tooltip></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </SoftBox>
              </PanelCard>
            </ClickableCard>
          </Grid>
        </Grid>

        {/* ── Row 3: Scout Reports ─────────────────────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <ClickableCard title="Scout Reports" icon="assignment" iconColor="dark" tooltip="Full reports with all attribute scores and notes" detailContent={reportsDetail}>
              <PanelCard title="Recent Scout Reports" subtitle="Filed this season — all scouts">
                <SoftBox display="flex" gap={2} sx={{ overflowX: "auto", pb: 1 }}>
                  {scoutReports.map((rep) => {
                    const rc = rep.recommendation === "Sign Now" ? "success" : rep.recommendation === "Monitor" ? "info" : "error";
                    return (
                      <Card key={rep.id} variant="outlined" sx={{ minWidth: 260, maxWidth: 280, flexShrink: 0, p: 2 }}>
                        <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                          <span style={{ fontSize: 20 }}>{rep.flag}</span>
                          <SoftBox><SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block" }}>{rep.player}</SoftTypography><SoftTypography variant="caption" color="secondary">{rep.club} · Age {rep.age}</SoftTypography></SoftBox>
                        </SoftBox>
                        <Divider sx={{ my: 0.75 }} />
                        <SoftTypography variant="caption" color="secondary" sx={{ display: "block", mb: 1 }}>{rep.scout} · {formatDateIN(rep.date)}</SoftTypography>
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: ratingColor(rep.overallRating) }}>{rep.overallRating.toFixed(1)}<span style={{ fontSize: "0.75rem", fontWeight: 400, color: "#8392ab" }}>/10</span></SoftTypography>
                          <SoftBadge badgeContent={rep.recommendation} color={rc} variant="gradient" size="xs" container />
                        </SoftBox>
                        {Object.entries(rep.attributes).map(([key, val]) => <AttrBar key={key} label={key} value={val} />)}
                        <Divider sx={{ my: 1 }} />
                        <SoftBox display="flex" justifyContent="space-between" mb={0.75}><SoftTypography variant="caption" color="secondary">Fee Est.</SoftTypography><SoftTypography variant="caption" fontWeight="medium">{rep.feeEstimate ? formatCompact(rep.feeEstimate) : "—"}</SoftTypography></SoftBox>
                        <SoftTypography variant="caption" color="secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{rep.notes}</SoftTypography>
                      </Card>
                    );
                  })}
                </SoftBox>
              </PanelCard>
            </ClickableCard>
          </Grid>
        </Grid>

        {/* ── Row 4: Scouting Alerts + Foreign Slot Status ─────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={6}>
            <ClickableCard title="Scouting Alerts" icon="notifications_active" iconColor="warning" tooltip="All alerts with action items" detailContent={alertsDetail}>
              <PanelCard title="Scouting Alerts" subtitle={`${scoutingAlerts.length} active notification${scoutingAlerts.length !== 1 ? "s" : ""}`}>
                {scoutingAlerts.length === 0 ? (
                  <SoftTypography variant="caption" color="secondary">No scouting alerts at this time.</SoftTypography>
                ) : scoutingAlerts.map((alert, idx) => (
                  <SoftBox key={alert.id} mb={1.5}>
                    <SoftBox display="flex" alignItems="flex-start" gap={1}>
                      <Icon fontSize="small" sx={{ color: alert.type === "error" ? "#ea0606" : alert.type === "warning" ? "#fbcf33" : "#17c1e8", mt: 0.25, flexShrink: 0 }}>{ALERT_ICON[alert.type] || "info"}</Icon>
                      <SoftBox flex={1}>
                        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                          <SoftTypography variant="caption" fontWeight="bold">{alert.title}</SoftTypography>
                          <SoftTypography variant="caption" color="secondary" sx={{ whiteSpace: "nowrap", ml: 1 }}>{alert.time}</SoftTypography>
                        </SoftBox>
                        <SoftTypography variant="caption" color="secondary">{alert.description}</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    {idx < scoutingAlerts.length - 1 && <Divider sx={{ mt: 1.25 }} />}
                  </SoftBox>
                ))}
              </PanelCard>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <ClickableCard title="Foreign Slot Status" icon="public" iconColor="error" tooltip="ISL foreign rules, slot usage and expiring contracts" detailContent={foreignSlotDetail}>
              <PanelCard title="Foreign Slot Status" subtitle="ISL 4+1 rule — max 6 registered foreign players">
                <SoftBox mb={1.5}>
                  <SoftBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                    <SoftBadge badgeContent={`${foreignPlayers.length}/6 slots used`} color="info" variant="gradient" size="sm" container />
                    <SoftBadge badgeContent={`${foreignPlayers.filter((p) => p.status === "Available").length} available`} color="success" variant="gradient" size="sm" container />
                    <SoftBadge badgeContent={`${foreignPlayers.filter((p) => p.status === "Injured").length} injured`} color="error" variant="gradient" size="sm" container />
                  </SoftBox>
                  <SoftTypography variant="caption" color="secondary" sx={{ display: "block", mb: 1 }}>4 foreign players + 1 Asian foreign player may play simultaneously (4+1 rule).</SoftTypography>
                </SoftBox>
                {foreignPlayers.map((p, idx) => {
                  const isExpiring = p.contractExpiry <= "2026-06-01";
                  const sc = p.status === "Available" ? "success" : p.status === "Injured" ? "error" : "warning";
                  return (
                    <SoftBox key={p.id}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
                        <SoftBox display="flex" alignItems="center" gap={1}>
                          <span style={{ fontSize: 16 }}>{p.flag}</span>
                          <SoftBox><SoftTypography variant="caption" fontWeight="medium" sx={{ display: "block" }}>{p.name}</SoftTypography><SoftTypography variant="caption" color="secondary">{p.position} · {p.nationality}</SoftTypography></SoftBox>
                        </SoftBox>
                        <SoftBox display="flex" gap={0.75} alignItems="center">
                          {isExpiring && <Tooltip title={`Contract expires ${p.contractExpiry}`}><SoftBadge badgeContent="Expiring" color="warning" variant="gradient" size="xs" container /></Tooltip>}
                          <SoftBadge badgeContent={p.status} color={sc} variant="gradient" size="xs" container />
                        </SoftBox>
                      </SoftBox>
                      {idx < foreignPlayers.length - 1 && <Divider />}
                    </SoftBox>
                  );
                })}
              </PanelCard>
            </ClickableCard>
          </Grid>
        </Grid>

      </SoftBox>

      {/* ── KPI Detail Dialog ─────────────────────────────────────────────── */}
      <Dialog open={Boolean(kpiModal)} onClose={() => setKpiModal(null)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle><SoftTypography variant="h6" fontWeight="bold" component="span">{kpiModal && KPI_TITLE[kpiModal]}</SoftTypography></DialogTitle>
        <DialogContent>{kpiModal && KPI_DETAILS[kpiModal]}</DialogContent>
        <DialogActions><SoftButton variant="gradient" color="dark" size="small" onClick={() => setKpiModal(null)}>Close</SoftButton></DialogActions>
      </Dialog>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default ScoutingDashboard;
